import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import { useMe } from '@/hooks/use-me';
import { DataResultSchema } from '@/warranty-v3/entities/data-result';
import type { SelectedFilters } from '@/warranty-v3/entities/filters';

import { getDeepestSelectedFilters } from '../utils/get-deepest-selected-filters';

import { dataResult } from './__mocked_data__/data-result';
import { dataResultMapper } from './mappers/data-result-mapper';

export namespace GetDataQuery {
  export const key = (input: Input) => [
    'warranty-v3',
    'data',
    input.systemId,
    input.selectedFilters,
  ];

  export type Input = {
    systemId: string;
    selectedFilters: SelectedFilters;
  };
  export const OutputSchema = DataResultSchema;
  export type Output = z.infer<typeof OutputSchema>;

  function useQueryFn() {
    const me = useMe();
    const warrantyDataRetrieve = useInjectFeApiContext(
      FeApi.warrantyV2DataRetrieve,
    );

    return async function queryFn(input: Input): Promise<Output> {
      if (me.config.id === '49') {
        return dataResult(input);
      }

      const deepestSelectedFilters = getDeepestSelectedFilters(
        input.selectedFilters,
      );

      const result = await warrantyDataRetrieve({
        ...deepestSelectedFilters,
        systemId: input.systemId,
      });

      if (result.status !== 200) {
        throw new Error('Failed to fetch data');
      }

      const output = await OutputSchema.safeParseAsync(
        dataResultMapper(result.data),
      );

      if (!output.success) {
        throw new Error('Failed to parse fe_api data', {
          cause: output.error,
        });
      }

      return output.data;
    };
  }

  export function useQuery(input: Input) {
    const queryFn = useQueryFn();

    return rq.useQuery<Output>({
      queryKey: key(input),
      queryFn: () => queryFn(input),
      keepPreviousData: true,
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(input: Input): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(input),
        queryFn: () => queryFn(input),
      });
    };
  }
}
